import { useRef, useEffect, useState } from 'react';
import Bubble from './Bubble';
import './InfoBlock.scss';

export default function InfoBlock({ text, pinMs = 2000 }) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState('right');

  const wrapRef = useRef(null);
  const bubbleRef = useRef(null);

  // runtime flags
  const pinnedRef = useRef(false); // true for the click "pinned" period
  const hoverRef = useRef(false); // pointer is currently over the icon
  const timerRef = useRef(null); // hide timer for the pinned state

  // Choose left/right so the bubble doesn't overflow viewport
  const updatePosition = () => {
    const icon = wrapRef.current;
    const bubble = bubbleRef.current;
    if (!icon || !bubble) return;

    const iconRect = icon.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();

    const gap = 16;
    const spaceRight = window.innerWidth - iconRect.right;
    const spaceLeft = iconRect.left;

    const fitsRight = bubbleRect.width + gap <= spaceRight;
    const fitsLeft = bubbleRect.width + gap <= spaceLeft;

    if (!fitsRight && fitsLeft) setPosition('left');
    else setPosition('right');
  };

  // Clear timer on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Keep position correct on resize/rotation while visible
  useEffect(() => {
    const onResize = () => show && updatePosition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [show]);

  // ---- Handlers ----

  const openOnHover = () => {
    hoverRef.current = true;
    updatePosition();
    setShow(true);
  };

  const closeOnHoverLeave = () => {
    hoverRef.current = false;
    // Do not hide if pinned by click
    if (!pinnedRef.current) setShow(false);
  };

  const pinByClick = () => {
    // Click should ALWAYS show and pin for pinMs, restarting the timer
    updatePosition();
    setShow(true);
    pinnedRef.current = true;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      pinnedRef.current = false;
      // If still hovered when pin expires, keep it open; otherwise hide
      setShow(hoverRef.current);
    }, pinMs);
  };

  return (
    <div
      className='info-block'
      ref={wrapRef}
      onMouseEnter={openOnHover}
      onFocus={openOnHover}
      onMouseLeave={closeOnHoverLeave}
      onBlur={closeOnHoverLeave}
      onClick={pinByClick}
    >
      <Bubble ref={bubbleRef} show={show} text={text} position={position} />
    </div>
  );
}
